import React, { useEffect, useId, useState } from "react";
import { spendingKeyToRecoveryPhrase } from "../../joinsplit-v2/recoveryPhrase";
import { styles } from "../../styles";
import type { MasterRecoveryMetadata } from "../../joinsplit-v2/masterRecoveryConnection";

export interface RecoveryPhraseBackupProps {
  spendingKey: Uint8Array | null;
  /** Original 128-bit recovery entropy for new 12-word wallets. */
  recoveryEntropy?: Uint8Array;
  /** A global phrase encodes the master, never its derived network key. */
  masterRecovery?: MasterRecoveryMetadata;
  scopeLabel: string;
  /** Public wallet identity and pool scope; never a secret. */
  walletIdentity: string;
  onBackupConfirmed?: () => void;
}

type BackupState = {
  key: Uint8Array | null;
  entropy?: Uint8Array;
  phase: "hidden" | "revealed" | "confirm" | "complete";
  words: string[];
  answers: string[];
  error: string;
};

const SHORT_CHECK_WORDS = [3, 7, 11] as const;
const LEGACY_CHECK_WORDS = [3, 11, 19] as const;
const emptyState = (
  key: Uint8Array | null,
  entropy?: Uint8Array,
): BackupState => ({
  key,
  entropy,
  phase: "hidden",
  words: [],
  answers: ["", "", ""],
  error: "",
});
const muted: React.CSSProperties = {
  color: "var(--wallet-muted, #b0c4de)",
  fontSize: 14,
  lineHeight: 1.5,
};
const actionStyle: React.CSSProperties = {
  ...styles.button,
  ...styles.secondaryButton,
  minHeight: 44,
  marginTop: 8,
};

/** Phrase text is created only on explicit reveal and never persisted here. */
export function RecoveryPhraseBackup(props: RecoveryPhraseBackupProps) {
  const master = props.masterRecovery?.session.wallet;
  return (
    <RecoveryPhraseBackupSession
      key={`${props.walletIdentity}:${props.scopeLabel}`}
      {...props}
      spendingKey={master?.rootKey ?? props.spendingKey}
      recoveryEntropy={master?.recoveryEntropy ?? props.recoveryEntropy}
    />
  );
}

function RecoveryPhraseBackupSession({
  spendingKey,
  recoveryEntropy,
  scopeLabel,
  onBackupConfirmed,
  masterRecovery,
}: RecoveryPhraseBackupProps) {
  const id = useId();
  const [state, setState] = useState<BackupState>(() =>
    emptyState(spendingKey, recoveryEntropy),
  );
  // Guard the render as well as clearing state: a key change must never expose
  // the previous wallet's phrase for even one committed render.
  const current =
    state.key === spendingKey && state.entropy === recoveryEntropy;
  const phase = current ? state.phase : "hidden";
  const checkWords =
    state.words.length === 12 ? SHORT_CHECK_WORDS : LEGACY_CHECK_WORDS;
  useEffect(() => {
    setState(emptyState(spendingKey, recoveryEntropy));
  }, [spendingKey, recoveryEntropy]);
  useEffect(() => {
    const hideWhenBackgrounded = () => {
      if (document.visibilityState === "hidden")
        setState(emptyState(spendingKey, recoveryEntropy));
    };
    document.addEventListener("visibilitychange", hideWhenBackgrounded);
    return () =>
      document.removeEventListener("visibilitychange", hideWhenBackgrounded);
  }, [spendingKey, recoveryEntropy]);

  const hide = () => setState(emptyState(spendingKey, recoveryEntropy));
  const reveal = () => {
    if (!spendingKey) return;
    try {
      const words = spendingKeyToRecoveryPhrase(
        spendingKey,
        recoveryEntropy,
      ).split(" ");
      setState({
        ...emptyState(spendingKey, recoveryEntropy),
        phase: "revealed",
        words,
      });
    } catch {
      setState({
        ...emptyState(spendingKey, recoveryEntropy),
        error:
          "Could not show this wallet's recovery phrase. Reconnect and retry.",
      });
    }
  };
  const confirm = () => {
    if (!current || phase !== "confirm") return;
    const matches = checkWords.every(
      (position, index) =>
        state.answers[index].trim().toLowerCase() === state.words[position],
    );
    if (!matches) {
      setState((value) => ({
        ...value,
        error:
          "Those words do not match. Check your saved phrase and try again.",
      }));
      return;
    }
    setState({
      ...emptyState(spendingKey, recoveryEntropy),
      phase: "complete",
    });
    onBackupConfirmed?.();
  };

  return (
    <section
      aria-labelledby={`${id}-heading`}
      style={{ ...styles.card, marginTop: 16, minWidth: 0 }}
    >
      <h3 id={`${id}-heading`} style={{ ...styles.cardTitle, marginTop: 0 }}>
        StealthOS recovery phrase
      </h3>
      <p style={muted}>
        {masterRecovery
          ? "One phrase restores your StealthOS wallet on Base, DogeOS and Solana."
          : `This is an older chain-specific wallet for ${scopeLabel}. Back up its phrase separately.`}
      </p>
      <p style={muted}>
        This is your StealthOS phrase, not your MetaMask or Phantom phrase.
        Anyone with it can access your private funds. Keep it somewhere private
        and safe.
      </p>
      <p style={muted}>
        {masterRecovery
          ? "Your private notes are recovered from encrypted data recorded on-chain. Keep all 12 words safe."
          : "This older wallet also needs its encrypted note backup or the recovery service. Its notes are not protected by phrase-only on-chain recovery."}
      </p>

      {phase === "hidden" ? (
        <button
          type="button"
          className="wallet-secondary"
          style={actionStyle}
          onClick={reveal}
          disabled={!spendingKey}
        >
          Show recovery phrase
        </button>
      ) : null}

      {phase === "revealed" ? (
        <>
          <p style={muted}>
            Write down all {state.words.length} words in this exact order.
          </p>
          <ol
            aria-label={`Your ${state.words.length} recovery words`}
            translate="no"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "8px 24px",
              paddingLeft: 28,
              overflowWrap: "anywhere",
              color: "var(--wallet-text, #e8eeff)",
              fontSize: 14,
            }}
          >
            {state.words.map((word, index) => (
              <li key={index}>{word}</li>
            ))}
          </ol>
          <button
            type="button"
            className="wallet-secondary"
            style={actionStyle}
            onClick={() =>
              setState((value) => ({ ...value, phase: "confirm", error: "" }))
            }
          >
            I saved the phrase
          </button>
        </>
      ) : null}

      {phase === "confirm" ? (
        <>
          <p style={muted}>Confirm these three words from your saved phrase.</p>
          {checkWords.map((position, index) => (
            <div key={position}>
              <label htmlFor={`${id}-word-${position}`} style={styles.label}>
                Word {position + 1}
              </label>
              <input
                id={`${id}-word-${position}`}
                type="text"
                value={state.answers[index]}
                onChange={(event) => {
                  const answer = event.target.value;
                  setState((value) => ({
                    ...value,
                    answers: value.answers.map((word, i) =>
                      i === index ? answer : word,
                    ),
                    error: "",
                  }));
                }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                data-lpignore="true"
                style={{ ...styles.input, outline: undefined }}
                aria-invalid={Boolean(state.error)}
                aria-describedby={state.error ? `${id}-error` : undefined}
              />
            </div>
          ))}
          <button
            type="button"
            className="wallet-secondary"
            style={actionStyle}
            onClick={confirm}
          >
            Confirm backup
          </button>
          <button
            type="button"
            className="wallet-secondary"
            style={actionStyle}
            onClick={reveal}
          >
            Show phrase again
          </button>
        </>
      ) : null}

      {phase === "revealed" || phase === "confirm" ? (
        <button
          type="button"
          className="wallet-secondary"
          style={actionStyle}
          onClick={hide}
        >
          Hide recovery phrase
        </button>
      ) : null}

      {phase === "complete" ? (
        <>
          <p role="status" style={muted}>
            {masterRecovery
              ? "Recovery phrase checked. Keep all 12 words safe."
              : "Recovery phrase checked. Keep it safe with your encrypted note backup."}
          </p>
          <button
            type="button"
            className="wallet-secondary"
            style={actionStyle}
            onClick={reveal}
          >
            Show recovery phrase
          </button>
        </>
      ) : null}
      {current && state.error ? (
        <p id={`${id}-error`} role="alert" style={{ color: "#ff7988" }}>
          {state.error}
        </p>
      ) : null}
    </section>
  );
}

export interface RecoveryPhraseInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
}

/** Controlled by its owner so connect/close can clear the secret immediately. */
export function RecoveryPhraseInput({
  value,
  onChange,
  disabled = false,
  error,
}: RecoveryPhraseInputProps) {
  const id = useId();
  return (
    <div style={{ marginTop: 16 }}>
      <label htmlFor={id} style={styles.label}>
        StealthOS recovery phrase
      </label>
      <p id={`${id}-help`} style={muted}>
        Enter your 12-word StealthOS recovery phrase for Base, DogeOS and
        Solana. Older 24-word phrases are also supported. Never enter your
        MetaMask or Phantom recovery phrase.
      </p>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        rows={4}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        data-lpignore="true"
        translate="no"
        aria-describedby={`${id}-help${error ? ` ${id}-error` : ""}`}
        aria-invalid={Boolean(error)}
        style={{ ...styles.input, resize: "vertical", outline: undefined }}
      />
      {error ? (
        <p id={`${id}-error`} role="alert" style={{ color: "#ff7988" }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
